/**
 * @packageDocumentation
 * Controls automated spin sessions, handling countdowns, UI state, and callbacks that drive repeated rounds without manual input.
 */
// Autoplay.ts
import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { CardScreen, onSpinClicked } from "./CardScreen";
import { betArea } from "./betArea";
import { betSizes } from "./server";
import { LogoAnim } from "../app";
import { disabledfilter, HandleOrientation } from "./global";
import { SoundManager } from "./sounds";

type SpinFn = () => Promise<void>;
type CountChoice = 5 | 10 | 15 | 20;

type Rect = { left: number; top: number; w: number; h: number };

type FigmaCfg = {
  BASE_W: number;
  BASE_H: number;
  ROOT_OFFSET_X: number;
  ROOT_OFFSET_Y: number;

  MODAL: Rect; // modal_autoPlay
  BG: Rect; // modal_large (autoplay_bg art)
  CLOSE: Rect;
  TITLE: Rect;

  BTN_PLAY: Rect;
  BTN_PLAY_BG: Rect;
  BTN_PLAY_TEXT: Rect;

  COUNT_5: Rect;
  COUNT_10: Rect;
  COUNT_15: Rect;
  COUNT_20: Rect;

  // number label rects (white text layer from Figma)
  LABEL_5: Rect;
  LABEL_10: Rect;
  LABEL_15: Rect;
  LABEL_20: Rect;
};

export class AutoplayManager extends PIXI.Container {
  private screen: CardScreen | null = null;
  private spin: SpinFn;
  private screenGetter?: () => CardScreen | undefined;
  private isScreenWired = false;

  // main toggle buttons (live on this container — app.ts positions/scales this widget)
  public autoBtn!: PIXI.Sprite; // "Autoplay_but"
  public stopBtn!: PIXI.Sprite; // "stop_autoplay_but"
  private countLabel!: PIXI.Text;

  // SoundPrompt-style popup roots (screen-space, not affected by this container's pos/scale)
  private popupRoot = new PIXI.Container(); // added/removed to stage when open/close
  private designRoot = new PIXI.Container(); // scaled/letterboxed to figma base

  // overlay + modal (live inside popupRoot/designRoot)
  private overlay!: PIXI.Graphics;
  private modal!: PIXI.Container;

  // modal internals (refs placed via Figma)
  private modalBg!: PIXI.Sprite;
  private titleText!: PIXI.Text;
  private closeBtn!: PIXI.Sprite;

  private countButtons = new Map<CountChoice, PIXI.Container>();

  private startBtn!: PIXI.Container;
  private startBg!: PIXI.Sprite;
  private startLabel!: PIXI.Text;

  // state
  private selected: CountChoice = 5;
  private isRunning = false;
  private inRound = false;
  private left = 0; // rounds left; 0 means stopped

  private isPopupAdded = false;
  private raf = 0;
  private onResizeBound = () => this.requestLayout();

  // selection filter (slightly darker)
  private readonly selectedDarkenFilter = new PIXI.ColorMatrixFilter();

  /**
   * Stores the CURRENT fitted base scales after every layout pass.
   * Hover/press always multiplies off these values so nothing "sticks" after resize/orientation/layout.
   */
  private countBaseScales = new Map<
    CountChoice,
    { btn: { x: number; y: number }; lbl: { x: number; y: number } }
  >();

  // ===== FIGMA SOURCE OF TRUTH =====
  // Landscape base: 1920 x 1080
  private readonly FIGMA_L: FigmaCfg = {
    BASE_W: 1920,
    BASE_H: 1080,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    MODAL: { left: 172, top: 197, w: 1596, h: 670 }, // modal_autoPlay
    BG: { left: 172, top: 219, w: 1596, h: 648 }, // modal_large
    CLOSE: { left: 1435, top: 197, w: 74, h: 75 }, // btn_close
    TITLE: { left: 751, top: 298, w: 417, h: 142 }, // text_AUTO PLAY

    BTN_PLAY: { left: 845, top: 668, w: 260, h: 127 },
    BTN_PLAY_BG: { left: 845, top: 678, w: 232, h: 117 },
    BTN_PLAY_TEXT: { left: 860, top: 668, w: 222, h: 108 }, // white PLAY

    COUNT_5: { left: 650, top: 484, w: 133, h: 133 },
    COUNT_10: { left: 817, top: 484, w: 133, h: 133 },
    COUNT_15: { left: 984, top: 484, w: 133, h: 133 },
    COUNT_20: { left: 1151, top: 484, w: 133, h: 133 },

    // white number rects (your figma)
    LABEL_5: { left: 668, top: 460, w: 104, h: 152 },
    LABEL_10: { left: 835, top: 460, w: 104, h: 152 },
    LABEL_15: { left: 1002, top: 460, w: 104, h: 152 },
    LABEL_20: { left: 1160, top: 460, w: 129, h: 152 },
  };

  // Portrait base: 1080 x 1920
  private readonly FIGMA_P: FigmaCfg = {
    BASE_W: 1080,
    BASE_H: 1920,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    MODAL: { left: -110, top: 531, w: 1332, h: 569 }, // modal_autoPlay
    BG: { left: -110, top: 559, w: 1332, h: 541 }, // modal_large
    CLOSE: { left: 935, top: 531, w: 74, h: 75 }, // BTN_close
    TITLE: { left: 319, top: 585, w: 417, h: 142 }, // text_title

    BTN_PLAY: { left: 413, top: 955, w: 260, h: 127 },
    BTN_PLAY_BG: { left: 413, top: 965, w: 232, h: 117 },
    BTN_PLAY_TEXT: { left: 431, top: 955, w: 222, h: 108 }, // white PLAY

    COUNT_5: { left: 218, top: 771, w: 133, h: 133 },
    COUNT_10: { left: 385, top: 771, w: 133, h: 133 },
    COUNT_15: { left: 552, top: 771, w: 133, h: 133 },
    COUNT_20: { left: 719, top: 771, w: 133, h: 133 },

    // white number rects (your figma)
    LABEL_5: { left: 233, top: 747, w: 104, h: 152 },
    LABEL_10: { left: 401, top: 747, w: 104, h: 152 },
    LABEL_15: { left: 567, top: 747, w: 104, h: 152 },
    LABEL_20: { left: 725, top: 747, w: 129, h: 152 },
  };

  constructor(screenOrGetter: CardScreen | (() => CardScreen | undefined), spinFn?: SpinFn) {
    super();

    if (typeof screenOrGetter === "function") {
      this.screenGetter = screenOrGetter;
      this.screen = this.screenGetter() ?? null;
    } else {
      this.screen = screenOrGetter ?? null;
    }

    this.spin = spinFn ?? (() => onSpinClicked(this.ensureScreen())) as SpinFn;

    // configure the "selected" filter once (slightly darker)
    this.selectedDarkenFilter.brightness(0.90, false);

    this.buildUI();
    this.tryWireScreen();

    HandleOrientation(this.PortraitAdjustFun.bind(this), this.landScapeAdjustFun.bind(this));
  }

  // ---------------- UI ----------------
  private buildUI() {
    this.sortableChildren = true;

    // 1) Toggle buttons (outside popup, live on this container)
    this.autoBtn = new PIXI.Sprite(Assets.get("Autoplay_but"));
    this.autoBtn.scale.set(1.25);
    this.autoBtn.eventMode = "static";
    this.autoBtn.cursor = "pointer";
    this.autoBtn.on("pointerdown", () => this.openModal());
    this.addChild(this.autoBtn);

    this.stopBtn = new PIXI.Sprite(Assets.get("stop_autoplay_but"));
    this.stopBtn.scale.set(1.25);
    this.stopBtn.visible = false;
    this.stopBtn.eventMode = "static";
    this.stopBtn.cursor = "pointer";
    this.stopBtn.on("pointerdown", () => this.stop());
    this.addChild(this.stopBtn);

    this.countLabel = new PIXI.Text(
      "",
      new PIXI.TextStyle({
        fill: 0xffffff,
        fontFamily: "PermanentMarker",
        fontSize: 20,
        fontWeight: "700",
        stroke: 0xffffff,
        align: "center",
      })
    );
    this.countLabel.anchor.set(0.5, 1);
    this.countLabel.visible = false;
    this.addChild(this.countLabel);

    // 2) Popup roots (SoundPrompt-style)
    this.popupRoot.sortableChildren = true;
    this.designRoot.sortableChildren = true;

    // 3) Overlay (click outside closes) — MUST be behind modal
    this.overlay = new PIXI.Graphics().beginFill(0x000000, 0.35).drawRect(0, 0, 10, 10).endFill();
    this.overlay.visible = false;
    this.overlay.eventMode = "static";
    this.overlay.cursor = "default";
    this.overlay.on("pointerdown", () => this.closeModal());
    this.overlay.zIndex = 0;
    this.popupRoot.addChild(this.overlay);

    // Add designRoot ABOVE overlay
    this.designRoot.zIndex = 1;
    this.popupRoot.addChild(this.designRoot);

    // 4) Modal container — lives inside designRoot
    this.modal = this.buildModal();
    this.modal.visible = false;
    this.designRoot.addChild(this.modal);

    // initial selection visuals
    this.applySelectionVisuals();

    // initial layout (buttons only, popup will layout on open too)
    this.layout();

    // resize/orientation listeners
    window.addEventListener?.("resize", this.onResizeBound);
    window.addEventListener?.("orientationchange", this.onResizeBound);
    window.visualViewport?.addEventListener?.("resize", this.onResizeBound);
    window.visualViewport?.addEventListener?.("scroll", this.onResizeBound);
  }

  // ---------------- Layout helpers ----------------
  private getScreenSize() {
    const app = (globalThis as any).app ?? (globalThis as any).__PIXI_APP__ ?? null;
    const screenW = app?.renderer?.screen?.width ?? (window.visualViewport?.width ?? window.innerWidth ?? 1920);
    const screenH = app?.renderer?.screen?.height ?? (window.visualViewport?.height ?? window.innerHeight ?? 1080);
    return { screenW, screenH };
  }

  private layoutDesignRoot(screenW: number, screenH: number, cfg: FigmaCfg) {
    const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);
    this.designRoot.scale.set(s);
    this.designRoot.x = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
    this.designRoot.y = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;
  }

  private figmaCenter(left: number, top: number, w: number, h: number) {
    return { x: left + w / 2, y: top + h / 2 };
  }

  private placeFromFigmaLTWH(
    obj: PIXI.Container,
    left: number,
    top: number,
    w: number,
    h: number,
    opts?: { fitToSize?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { fitToSize = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    if (fitToSize && (obj as any).width != null && (obj as any).height != null) {
      const anyObj = obj as any as { width: number; height: number; scale: PIXI.ObservablePoint };
      const sx = w / Math.max(anyObj.width, 0.0001);
      const sy = h / Math.max(anyObj.height, 0.0001);
      anyObj.scale.set(anyObj.scale.x * sx, anyObj.scale.y * sy);
    }

    const b = obj.getLocalBounds();
    obj.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

    const c = this.figmaCenter(left, top, w, h);
    obj.position.set(c.x + nudgeX, c.y + nudgeY);
  }

  /**
   * Place anchored text by its own Figma LTWH (centers the text within that rect and scales to fit).
   */
  private placeAnchoredTextFromFigma(text: PIXI.Text, left: number, top: number, w: number, h: number) {
    text.anchor.set(0.5);
    text.scale.set(1);

    const tw = Math.max(1, text.width);
    const th = Math.max(1, text.height);

    const s = Math.min(w / tw, h / th);
    text.scale.set(s);

    const cx = left + w / 2;
    const cy = top + h / 2;
    text.position.set(cx, cy);
  }

  private requestLayout() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.layout());
  }

  private layout() {
    const { screenW, screenH } = this.getScreenSize();

    // Stop button stays exactly where autoplay button is (this container is button-space)
    this.stopBtn.position.copyFrom(this.autoBtn.position);
    const stopWidth = this.stopBtn.width ?? 0;
    this.countLabel.position.set(this.stopBtn.x + stopWidth / 2, 55);

    if (!this.modal || !this.overlay) return;

    // Overlay always matches the real screen in popupRoot (screen-space)
    this.overlay.clear().beginFill(0x000000, 0.35).drawRect(0, 0, screenW, screenH).endFill();
    this.overlay.hitArea = new PIXI.Rectangle(0, 0, screenW, screenH);
    this.overlay.position.set(0, 0);

    const portrait = screenH > screenW;
    const cfg = portrait ? this.FIGMA_P : this.FIGMA_L;

    // Letterbox + scale designRoot to cfg base
    this.layoutDesignRoot(screenW, screenH, cfg);

    // Place modal container
    this.modal.scale.set(1);
    this.placeFromFigmaLTWH(this.modal, cfg.MODAL.left, cfg.MODAL.top, cfg.MODAL.w, cfg.MODAL.h);

    // Background
    this.modalBg.scale.set(1);
    this.placeFromFigmaLTWH(this.modalBg, cfg.BG.left, cfg.BG.top, cfg.BG.w, cfg.BG.h, { fitToSize: true });

    // Title
    this.titleText.scale.set(1);
    this.placeFromFigmaLTWH(this.titleText, cfg.TITLE.left, cfg.TITLE.top, cfg.TITLE.w, cfg.TITLE.h, { fitToSize: true });

    // Close
    this.closeBtn.scale.set(1);
    this.placeFromFigmaLTWH(this.closeBtn, cfg.CLOSE.left, cfg.CLOSE.top, cfg.CLOSE.w, cfg.CLOSE.h, { fitToSize: true });

    // Counts
    const buttonRectByValue: Record<number, Rect> = {
      5: cfg.COUNT_5,
      10: cfg.COUNT_10,
      15: cfg.COUNT_15,
      20: cfg.COUNT_20,
    };

    const labelRectByValue: Record<number, Rect> = {
      5: cfg.LABEL_5,
      10: cfg.LABEL_10,
      15: cfg.LABEL_15,
      20: cfg.LABEL_20,
    };

    for (const [val, cell] of this.countButtons.entries()) {
      const btnRect = buttonRectByValue[val];
      const lblRect = labelRectByValue[val];

      // This container is ONLY for interactivity. We keep it neutral (no bounds/pivot math).
      cell.position.set(0, 0);
      cell.pivot.set(0, 0);
      cell.scale.set(1);
      cell.alpha = 1;

      // Hit area in MODAL local space so pointer events work even though children are positioned in modal space
      cell.hitArea = new PIXI.Rectangle(btnRect.left, btnRect.top, btnRect.w, btnRect.h);

      // Place the yellow box ONLY from COUNT_x
      const btnSprite = cell.getChildAt(0) as PIXI.Sprite | undefined;
      if (btnSprite) {
        btnSprite.scale.set(1);
        this.placeFromFigmaLTWH(btnSprite as any, btnRect.left, btnRect.top, btnRect.w, btnRect.h, { fitToSize: true });
      }

      // Place the number ONLY from LABEL_x
      const label = cell.getChildAt(1) as PIXI.Text | undefined;
      if (label) {
        this.placeAnchoredTextFromFigma(label, lblRect.left, lblRect.top, lblRect.w, lblRect.h);
      }

      // Record CURRENT fitted base scales (prevents hover from “sticking” after layout/resize)
      if (btnSprite && label) {
        this.countBaseScales.set(val, {
          btn: { x: btnSprite.scale.x, y: btnSprite.scale.y },
          lbl: { x: label.scale.x, y: label.scale.y },
        });
      }
    }

    // Start button container
    const play = cfg.BTN_PLAY;
    this.startBtn.scale.set(1);
    this.placeFromFigmaLTWH(this.startBtn, play.left, play.top, play.w, play.h);

    // Start background (purple rect)
    const playBg = cfg.BTN_PLAY_BG;
    this.startBg.scale.set(1);
    this.placeFromFigmaLTWH(this.startBg, playBg.left, playBg.top, playBg.w, playBg.h, { fitToSize: true });

    // Start label using anchored text placement
    const playTxt = cfg.BTN_PLAY_TEXT;
    this.placeAnchoredTextFromFigma(this.startLabel, playTxt.left, playTxt.top, playTxt.w, playTxt.h);

    // keep selection visuals correct (in case layout rebuilt sizes)
    this.applySelectionVisuals();
  }

  // ---------------- Selection visuals ----------------
  private setCountCellSelected(cell: PIXI.Container, selected: boolean) {
    // Apply filter to the button sprite only so the number text stays crisp/white
    const btnSprite = cell.getChildAt(0) as PIXI.Sprite | undefined;
    if (!btnSprite) return;

    if (selected) btnSprite.filters = [this.selectedDarkenFilter];
    else btnSprite.filters = [];
  }

  private applySelectionVisuals() {
    for (const [val, cell] of this.countButtons.entries()) {
      this.setCountCellSelected(cell, val === this.selected);
    }
  }

  // ---------------- Hover/Press (subtle, never “sticks”) ----------------
  private addHoverPressToCountCell(cell: PIXI.Container, value: CountChoice) {
    const btn = cell.getChildAt(0) as PIXI.Sprite | undefined;
    const label = cell.getChildAt(1) as PIXI.Text | undefined;
    if (!btn) return;

    // Subtle values
    const HOVER_SCALE = 1.015;
    const PRESS_SCALE = 0.985;
    const HOVER_ALPHA = 0.99;

    const setToBase = () => {
      const base = this.countBaseScales.get(value);
      if (!base) return;
      btn.scale.set(base.btn.x, base.btn.y);
      if (label) label.scale.set(base.lbl.x, base.lbl.y);
    };

    const setMul = (mul: number) => {
      const base = this.countBaseScales.get(value);
      if (!base) return;
      btn.scale.set(base.btn.x * mul, base.btn.y * mul);
      if (label) label.scale.set(base.lbl.x * mul, base.lbl.y * mul);
    };

    // Make sure the interactive target is the cell
    cell.eventMode = "static";
    cell.cursor = "pointer";

    cell.on("pointerover", () => {
      setMul(HOVER_SCALE);
      cell.alpha = HOVER_ALPHA;
    });

    cell.on("pointerout", () => {
      setToBase();
      cell.alpha = 1;
    });

    cell.on("pointerdown", () => {
      SoundManager.Btnclick_1SndPlay();
      this.selected = value;
      this.applySelectionVisuals();

      setMul(PRESS_SCALE);
      cell.alpha = 1;
    });

    cell.on("pointerup", () => {
      setMul(HOVER_SCALE);
      cell.alpha = HOVER_ALPHA;
    });

    cell.on("pointerupoutside", () => {
      setToBase();
      cell.alpha = 1;
    });
  }

  // ---------------- Modal building ----------------
  private buildModal(): PIXI.Container {
    const c = new PIXI.Container();
    c.sortableChildren = true;

    // Background art (autoplay_bg)
    this.modalBg = new PIXI.Sprite(Assets.get("autoplay_bg"));
    this.modalBg.interactive = true;
    this.modalBg.zIndex = 1;
    c.addChild(this.modalBg);

    // Title
    this.titleText = new PIXI.Text(
      "AUTO PLAY",
      new PIXI.TextStyle({
        fill: 0xffffff,
        fontSize: 150,
        fontWeight: "400",
        fontFamily: "RoadRage",
        lineHeight: 165,
        dropShadow: { alpha: 1, angle: 1, blur: 2, color: "0x111111", distance: 2 },
        stroke: { color: "black", width: 5, join: "round" },
      })
    );
    this.titleText.anchor.set(0.5);
    this.titleText.zIndex = 2;
    c.addChild(this.titleText);

    // Close button
    this.closeBtn = new PIXI.Sprite(Assets.get("close_btn"));
    this.closeBtn.eventMode = "static";
    this.closeBtn.cursor = "pointer";
    this.closeBtn.on("pointerdown", () => this.closeModal());
    this.closeBtn.zIndex = 3;
    c.addChild(this.closeBtn);

    // Count button cells (containers exist only for hitArea + pointer events)
    const counts: CountChoice[] = [5, 10, 15, 20];

    counts.forEach((value) => {
      const cell = new PIXI.Container();
      cell.sortableChildren = true;

      const btn = new PIXI.Sprite(Assets.get("but_autocount"));
      btn.zIndex = 1;
      cell.addChild(btn);

      const label = new PIXI.Text(
        String(value),
        new PIXI.TextStyle({
          fill: 0xffffff,
          fontSize: 60,
          fontWeight: "400",
          fontFamily: "PermanentMarker",
          lineHeight: 117,
          dropShadow: { alpha: 1, angle: 1, blur: 2, color: "0x111111", distance: 2 },
          stroke: { color: "black", width: 5, join: "round" },
        })
      );
      label.anchor.set(0.5);
      label.zIndex = 2;
      cell.addChild(label);

      // Hover/press/select behavior
      this.addHoverPressToCountCell(cell, value);

      cell.zIndex = 3;
      c.addChild(cell);

      this.countButtons.set(value, cell);
    });

    // Start button container
    this.startBtn = new PIXI.Container();
    this.startBtn.eventMode = "static";
    this.startBtn.cursor = "pointer";
    this.startBtn.zIndex = 4;

    this.startBg = new PIXI.Sprite(Assets.get("but_start_autoplay"));
    this.startBg.zIndex = 1;
    this.startBtn.addChild(this.startBg);

    this.startLabel = new PIXI.Text(
      "PLAY",
      new PIXI.TextStyle({
        fill: 0xffffff,
        fontSize: 125,
        fontWeight: "400",
        fontFamily: "RoadRage",
        lineHeight: 138,
        dropShadow: { alpha: 1, angle: 1, blur: 2, color: "0x111111", distance: 2 },
        stroke: { color: "black", width: 5, join: "round" },
      })
    );
    this.startLabel.anchor.set(0.5);
    this.startLabel.zIndex = 2;
    this.startBtn.addChild(this.startLabel);

    // feedback & action
    this.startBtn.on("pointerdown", () => {
      this.startBtn.scale.set(0.95);
    });
    this.startBtn.on("pointerup", () => {
      this.startBtn.scale.set(1);
      this.start(this.selected);
      this.closeModal();
    });
    this.startBtn.on("pointerupoutside", () => {
      this.startBtn.scale.set(1);
    });

    c.addChild(this.startBtn);

    return c;
  }

  // ---------------- Popup open/close (SoundPrompt-style) ----------------
  private openModal() {
    SoundManager.Btnclick_1SndPlay();

    const app = (globalThis as any).app ?? (globalThis as any).__PIXI_APP__;
    const stage = app?.stage ?? this.parent ?? undefined;

    if (stage && !this.isPopupAdded) {
      stage.addChild(this.popupRoot);
      this.isPopupAdded = true;
    }

    this.overlay.visible = true;
    this.modal.visible = true;

    // ensure ordering safe
    this.popupRoot.sortChildren();

    // ensure selection visuals correct when opening
    this.applySelectionVisuals();

    // layout will also refresh base scales
    this.layout();
  }

  private closeModal() {
    SoundManager.Btnclick_1SndPlay();

    this.overlay.visible = false;
    this.modal.visible = false;

    if (this.isPopupAdded) {
      this.popupRoot.parent?.removeChild(this.popupRoot);
      this.isPopupAdded = false;
    }
  }

  // ---------------- Screen resolution helpers ----------------
  private resolveScreen(): CardScreen | null {
    if (this.screen) return this.screen;
    if (this.screenGetter) {
      const resolved = this.screenGetter();
      if (resolved) {
        this.screen = resolved;
        return resolved;
      }
    }
    return null;
  }

  private ensureScreen(): CardScreen {
    const screen = this.resolveScreen();
    if (!screen) throw new Error("CardScreen not ready for autoplay");
    return screen;
  }

  private tryWireScreen() {
    if (this.isScreenWired) return;
    const screen = this.resolveScreen();
    if (!screen) return;

    screen.on?.("roundstart", () => {
      this.inRound = true;
    });

    screen.on?.("roundend", async () => {
      this.inRound = false;
      if (!this.isRunning) return;

      if (this.left > 0) this.left--;
      this.updateCountLabel();

      if (this.left === 0) {
        this.stop();
        if (LogoAnim.insideBonus) {
          LogoAnim.StartBut_disableFun?.();
        }
        return;
      }

      await this.maybeSpin();
    });

    this.isScreenWired = true;
  }

  private lockManualUi(lock: boolean) {
    if (lock) {
      LogoAnim.StartBut_disableFun?.();
    } else if (!this.isRunning) {
      LogoAnim.StartBut_enableFun?.();
    }

    const betUi = (window as any)?.betArea as betArea | undefined;
    if (!betUi) return;

    const toggleButton = (container?: PIXI.Container, enabled = false) => {
      if (!container) return;
      container.interactive = enabled;
      (container as any).cursor = enabled ? "pointer" : "not-allowed";
      container.filters = enabled ? [] : [disabledfilter];
    };

    const plusEnabled = !lock && betArea.currentBetIndex < betSizes.length - 1;
    const minusEnabled = !lock && betArea.currentBetIndex > 0;

    toggleButton(betUi.plusContainer, plusEnabled);
    toggleButton(betUi.minusContainer, minusEnabled);

    if (betUi.betTextContainer) {
      betUi.betTextContainer.interactive = !lock;
      (betUi.betTextContainer as any).cursor = !lock ? "pointer" : "not-allowed";
      betUi.betTextContainer.filters = !lock ? [] : [disabledfilter];
    }
  }

  private updateCountLabel() {
    if (!this.countLabel) return;
    this.countLabel.text = `${Math.max(this.left, 0)}`;
    this.countLabel.visible = this.isRunning && this.left > 0;
  }

  private async maybeSpin() {
    this.tryWireScreen();
    if (!this.isRunning || this.inRound) return;

    if (!this.resolveScreen() || LogoAnim.insideBonus) {
      setTimeout(() => {
        if (this.isRunning) this.maybeSpin();
      }, 16);
      return;
    }

    try {
      await this.spin();
    } catch (err) {
      console.warn("Autoplay spin failed:", err);
      this.stop();
    }
  }

  // ---------------- Public API ----------------
  public start(count: CountChoice) {
    if (this.isRunning) return;
    SoundManager.Btnclick_1SndPlay();

    this.left = count;
    this.isRunning = true;

    this.tryWireScreen();
    this.lockManualUi(true);

    this.autoBtn.visible = false;
    this.stopBtn.visible = true;

    this.updateCountLabel();
    this.maybeSpin();

    this.emit?.("autoplay:start", { left: this.left });
  }

  public stop() {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.left = 0;

    this.lockManualUi(false);

    this.stopBtn.visible = false;
    this.autoBtn.visible = true;

    this.updateCountLabel();
    this.emit?.("autoplay:stop");
  }

  public isRunningNow(): boolean {
    return this.isRunning;
  }

  public open() {
    this.openModal();
  }

  public syncButtons() {
    this.layout();
  }

  // Orientation callbacks (we now use figma portrait/landscape automatically in layout)
  PortraitAdjustFun() {
    this.requestLayout();
  }

  landScapeAdjustFun() {
    this.requestLayout();
  }

  override destroy(options?: boolean | PIXI.DestroyOptions) {
    // remove listeners
    window.removeEventListener?.("resize", this.onResizeBound);
    window.removeEventListener?.("orientationchange", this.onResizeBound);
    window.visualViewport?.removeEventListener?.("resize", this.onResizeBound);
    window.visualViewport?.removeEventListener?.("scroll", this.onResizeBound);
    if (this.raf) cancelAnimationFrame(this.raf);

    // ensure popup not left on stage
    if (this.isPopupAdded) {
      this.popupRoot.parent?.removeChild(this.popupRoot);
      this.isPopupAdded = false;
    }

    super.destroy(options as any);
  }
}
